<section
  class="workflow-semantic-diff"
  aria-labelledby="workflow-semantic-diff-title"
  [attr.aria-busy]="isLoading() ? 'true' : null"
  data-testid="workflow-semantic-diff"
>
  <header class="workflow-semantic-diff__header">
    <div>
      <h4 id="workflow-semantic-diff-title">Saved draft changes</h4>
      <p *ngIf="comparison() as result; else comparisonScope">
        Saved revision {{ result.draftRevision }} ·
        {{ result.changes.length }} change{{
          result.changes.length === 1 ? "" : "s"
        }}.
        <span *ngIf="hasPublishedBaseline(); else newWorkflowBaseline">
          Compared with published {{ baselineLabel() }}.
        </span>
        <ng-template #newWorkflowBaseline>
          New workflow · no published baseline. Review what this draft
          introduces.
        </ng-template>
      </p>
      <ng-template #comparisonScope>
        <p>Compare the saved draft with its published baseline.</p>
      </ng-template>
    </div>
    <button
      uiButton
      type="button"
      class="workflow-semantic-diff__button"
      [disabled]="isLoading()"
      (click)="retry()"
    >
      {{ isLoading() ? "Comparing..." : "Refresh comparison" }}
    </button>
  </header>
  <p class="workflow-semantic-diff__scope">
    This review includes saved changes only. Save local edits to include them in
    the comparison.
  </p>

  <p
    *ngIf="isLoading()"
    class="workflow-semantic-diff__state"
    role="status"
    aria-live="polite"
    data-testid="semantic-diff-loading"
  >
    Comparing saved revision {{ draft?.revision }} with its baseline.
  </p>

  <div
    *ngIf="error() as comparisonErrorValue"
    #comparisonError
    class="workflow-semantic-diff__error"
    role="alert"
    aria-live="assertive"
    tabindex="-1"
    data-testid="semantic-diff-error"
  >
    <strong>{{ comparisonErrorValue.title }}</strong>
    <ul>
      <li *ngFor="let message of comparisonErrorValue.messages">
        {{ message }}
      </li>
    </ul>
    <p *ngIf="comparison()">
      The last successful comparison remains available and is marked stale.
    </p>
    <button
      uiButton
      type="button"
      class="workflow-semantic-diff__button"
      [disabled]="isLoading()"
      (click)="retry()"
    >
      Retry comparison
    </button>
  </div>

  <p
    *ngIf="isStale()"
    class="workflow-semantic-diff__warning"
    role="status"
    aria-live="polite"
    data-testid="semantic-diff-stale"
  >
    <strong>{{
      staleReason() === "local_changes"
        ? "Local edits are not included."
        : "This comparison is not current."
    }}</strong>
    It describes saved revision {{ loadedDraftKey()?.revision }}.
    <ng-container [ngSwitch]="staleReason()">
      <span *ngSwitchCase="'local_changes'"
        >It does not include the current local edits.</span
      >
      <span *ngSwitchCase="'saved_revision'"
        >It is not the latest saved revision.</span
      >
      <span *ngSwitchCase="'process_settings'"
        >The process settings changed. Refresh this comparison to review the
        current settings.</span
      >
      <span *ngSwitchCase="'refresh_failed'">
        The latest refresh failed, so this retained result has not been newly
        verified.
      </span>
    </ng-container>
  </p>

  <ng-container *ngIf="comparison() as result">
    <ul
      *ngIf="result.changes.length > 0"
      class="workflow-semantic-diff__counts"
      aria-label="Saved behavior changes by type"
    >
      <li>
        <strong>{{ changeTypeCounts().added }}</strong> added
      </li>
      <li>
        <strong>{{ changeTypeCounts().changed }}</strong> changed
      </li>
      <li>
        <strong>{{ changeTypeCounts().removed }}</strong> removed
      </li>
    </ul>

    <p
      *ngIf="result.isIdentical && result.changes.length === 0"
      class="workflow-semantic-diff__empty"
      role="status"
      aria-live="polite"
      data-testid="semantic-diff-identical"
    >
      <strong>No behavior changes.</strong>
      <ng-container
        *ngIf="hasPublishedBaseline(); else identicalWithoutBaseline"
      >
        The saved draft has the same behavior as {{ baselineLabel() }}.
      </ng-container>
      <ng-template #identicalWithoutBaseline>
        No published baseline is available for this workflow.
      </ng-template>
    </p>

    <div
      *ngIf="!result.isIdentical && result.changes.length === 0"
      class="workflow-semantic-diff__empty"
      role="status"
      aria-live="polite"
      data-testid="semantic-diff-empty"
    >
      <strong>No behavior changes were found.</strong>
      <ng-container *ngIf="hasPublishedBaseline(); else emptyNewWorkflow">
        The saved content differs from {{ baselineLabel() }}, but the comparison
        reports no behavior changes.
      </ng-container>
      <ng-template #emptyNewWorkflow>
        This new workflow has no published baseline. The comparison reports no
        behavior changes.
      </ng-template>
      <details class="workflow-semantic-diff__empty-details">
        <summary>Technical comparison note</summary>
        A different canonical content hash can reflect behavior-neutral
        collection order or set representation changes.
      </details>
    </div>

    <div
      *ngIf="result.changes.length > 0"
      class="workflow-semantic-diff__workspace"
      data-testid="semantic-change-workspace"
    >
      <nav
        class="workflow-semantic-diff__areas"
        aria-labelledby="semantic-change-areas-title"
      >
        <header>
          <h5 id="semantic-change-areas-title">Changed areas</h5>
          <span>{{ changeGroups().length }}</span>
        </header>
        <button
          uiButton
          *ngFor="let group of changeGroups(); trackBy: trackGroup"
          type="button"
          [class.workflow-semantic-diff__area--selected]="
            selectedGroup().key === group.key
          "
          [attr.aria-pressed]="selectedGroup().key === group.key"
          (click)="selectGroup(group)"
        >
          <span>
            <strong>{{ group.label }}</strong>
            <small>{{ group.changes.length }}</small>
          </span>
          <span>{{ group.description }}</span>
        </button>
      </nav>

      <section
        class="workflow-semantic-diff__changes"
        aria-labelledby="semantic-change-list-title"
      >
        <header>
          <div>
            <h5 id="semantic-change-list-title">{{ selectedGroup().label }}</h5>
            <p>{{ selectedGroup().description }}</p>
          </div>
          <span>{{ selectedGroup().changes.length }}</span>
        </header>
        <div class="workflow-semantic-diff__change-list">
          <button
            uiButton
            *ngFor="let change of selectedGroup().changes; trackBy: trackChange"
            type="button"
            [class.workflow-semantic-diff__change--selected]="
              isSelectedChange(change)
            "
            [attr.aria-pressed]="isSelectedChange(change)"
            (click)="selectChange(change, true)"
          >
            <span class="workflow-semantic-diff__change-heading">
              <span [attr.data-change-type]="change.changeTypeCode">
                {{ displayCode(change.changeTypeCode) }}
              </span>
              <small>{{ changeLocation(change) }}</small>
            </span>
            <strong>{{ changeTitle(change) }}</strong>
          </button>
        </div>
      </section>

      <section
        *ngIf="selectedChange() as change"
        class="workflow-semantic-diff__inspector"
        aria-labelledby="semantic-change-inspector-title"
      >
        <header>
          <div>
            <span class="workflow-semantic-diff__eyebrow">
              {{ changeLocation(change) }}
            </span>
            <h5
              #changeInspectorHeading
              id="semantic-change-inspector-title"
              tabindex="-1"
            >
              {{ changeTitle(change) }}
            </h5>
          </div>
          <button
            uiButton
            *ngIf="!isPlatformSettingsChange(change)"
            type="button"
            class="workflow-semantic-diff__button"
            [disabled]="interactionBlocked || editorHandoffUnavailable()"
            (click)="focusChange(change)"
          >
            {{ editorActionLabel(change) }}
          </button>
        </header>
        <p
          *ngIf="isPlatformSettingsChange(change)"
          class="workflow-semantic-diff__value-note"
          data-testid="semantic-diff-platform-settings"
        >
          These process settings are managed automatically and saved with this
          workflow version when it is published.
        </p>
        <p
          *ngIf="editorHandoffUnavailable()"
          class="workflow-semantic-diff__value-note"
        >
          The saved revision changed. Refresh this comparison before opening its
          changes in the editor.
        </p>

        <ng-container *ngIf="selectedValues() as values">
          <dl class="workflow-semantic-diff__values">
            <div>
              <dt>
                {{
                  hasPublishedBaseline()
                    ? "Published baseline"
                    : "Before this draft"
                }}
              </dt>
              <dd
                [class.workflow-semantic-diff__absent]="
                  values.before.kind === 'absent' ||
                  values.before.kind === 'empty'
                "
              >
                <p>{{ values.before.text }}</p>
                <dl
                  *ngIf="values.before.facts.length"
                  class="workflow-semantic-diff__facts"
                >
                  <div *ngFor="let fact of values.before.facts">
                    <dt>{{ fact.label }}</dt>
                    <dd>{{ fact.value }}</dd>
                  </div>
                </dl>
              </dd>
            </div>
            <div>
              <dt>Saved draft · revision {{ result.draftRevision }}</dt>
              <dd
                [class.workflow-semantic-diff__absent]="
                  values.after.kind === 'absent' ||
                  values.after.kind === 'empty'
                "
              >
                <p>{{ values.after.text }}</p>
                <dl
                  *ngIf="values.after.facts.length"
                  class="workflow-semantic-diff__facts"
                >
                  <div *ngFor="let fact of values.after.facts">
                    <dt>{{ fact.label }}</dt>
                    <dd>{{ fact.value }}</dd>
                  </div>
                </dl>
              </dd>
            </div>
          </dl>
          <p
            *ngIf="
              values.before.kind === 'structured' ||
              values.after.kind === 'structured'
            "
            class="workflow-semantic-diff__value-note"
          >
            These summaries describe the configuration. Expand Technical details
            to inspect every field and exact value.
          </p>
        </ng-container>

        <details class="workflow-semantic-diff__technical">
          <summary>Technical details</summary>
          <dl>
            <div>
              <dt>Comparison summary</dt>
              <dd>{{ change.summary }}</dd>
            </div>
            <div>
              <dt>Element type</dt>
              <dd>
                <code>{{ change.elementTypeCode }}</code>
              </dd>
            </div>
            <div>
              <dt>Stable element ID</dt>
              <dd>
                <code>{{ change.elementId || "Not provided" }}</code>
              </dd>
            </div>
            <div>
              <dt>Document path</dt>
              <dd>
                <code>{{ change.path }}</code>
              </dd>
            </div>
            <div>
              <dt>
                {{
                  hasPublishedBaseline()
                    ? "Exact published value"
                    : "Exact before value"
                }}
              </dt>
              <dd>
                <code>{{
                  change.beforeValue === null
                    ? "Not present"
                    : change.beforeValue === ""
                      ? "Empty value"
                      : change.beforeValue
                }}</code>
              </dd>
            </div>
            <div>
              <dt>Exact saved value</dt>
              <dd>
                <code>{{
                  change.afterValue === null
                    ? "Not present"
                    : change.afterValue === ""
                      ? "Empty value"
                      : change.afterValue
                }}</code>
              </dd>
            </div>
          </dl>
        </details>
      </section>
    </div>
  </ng-container>
</section>
